import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function ReadOnlyGitHubAuditPage() {
  const faqs = [
    {
      question: 'Does EvidentTrail require write access?',
      answer: 'No. The GitHub App is read-only and does not modify code or settings.'
    },
    {
      question: 'Do you store source code?',
      answer: 'No. We store metadata only, not source code content or diffs.'
    },
    {
      question: 'What evidence can I export?',
      answer: 'Approvals, review timestamps, branch protection settings, and CI check status metadata.'
    },
    {
      question: 'Is guest mode supported?',
      answer: 'Yes. Guest mode supports one report for one repository with short retention.'
    }
  ];

  const longformSections = [
    {
      title: 'What auditors ask for',
      paragraphs: [
        'Read-only evidence requests focus on approvals, required checks, and branch protection settings without granting write access.'
      ],
      bullets: [
        'Approval evidence with timestamps',
        'Required check status metadata',
        'Branch protection settings in effect'
      ]
    },
    {
      title: 'Evidence artifacts included',
      paragraphs: [
        'Evidence packs include PR approvals, required checks, branch protection snapshots, and exception records when present.'
      ],
      bullets: [
        'PR approvals and reviewer identities',
        'Required checks status',
        'Branch protection rulesets',
        'Exception records when bypasses occur'
      ]
    },
    {
      title: 'How EvidentTrail works (read-only)',
      paragraphs: [
        'EvidentTrail uses read-only GitHub App access and captures metadata only. No write permissions are required.'
      ],
      bullets: [
        'Read-only GitHub App connection',
        'Metadata-only evidence capture',
        'Exportable packs with checksums'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Use approval timestamps and check status summaries to show controls operated correctly during the audit window.'
      ],
      bullets: [
        'Approvals are logged before merge',
        'Required checks are marked as passed',
        'Checksums validate export integrity'
      ]
    },
    {
      title: 'Common failure modes',
      paragraphs: [
        'Screenshots without timestamps or missing approvals create evidence gaps. Evidence packs provide a verifiable metadata trail.'
      ],
      bullets: [
        'Approval evidence missing for sampled changes',
        'Branch protection settings not documented',
        'Exports without a checksum trail'
      ]
    }
  ];

  return (
    <MarketingLayout
      title="Read-Only GitHub Audit & Evidence Collection | EvidentTrail"
      description="Securely audit GitHub repositories without write access. EvidentTrail uses a strictly read-only GitHub App to collect evidence without modifying code."
      h1="Read-Only GitHub Audit & Evidence Collection"
      heroText="Audit your software supply chain without giving external tools write access to your code."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Read-Only GitHub Audit', url: 'https://evidenttrail.com/read-only-github-audit' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Why Read-Only Matters</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Security teams are rightly skeptical of connecting third-party apps to core IP.
              EvidentTrail is designed with a "Least Privilege" architecture.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              <div className={tokens.card}>
                <h3 className="text-white font-bold mb-2">No Write Access</h3>
                <p className="text-sm text-slate-400">
                  Our GitHub App requests <strong>Read</strong> permissions only. We cannot merge code, change settings, or modify your repository in any way.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className="text-white font-bold mb-2">No Code Storage</h3>
                <p className="text-sm text-slate-400">
                  We process metadata (timestamps, authors, SHAs). Source code is never persisted in our database.
                </p>
              </div>
            </div>

            <h2 className={`${tokens.heading.h2} mb-6`}>What We Read</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="py-3 text-white font-semibold">Scope</th>
                    <th className="py-3 text-white font-semibold">Permission Level</th>
                    <th className="py-3 text-white font-semibold">Purpose</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-slate-300">
                  <tr className="border-b border-slate-700/50">
                    <td className="py-3">Pull Requests</td>
                    <td className="py-3"><span className="bg-green-900/50 text-green-400 px-2 py-1 rounded text-xs">Read</span></td>
                    <td className="py-3">Verify titles, descriptions, and linkage to tickets.</td>
                  </tr>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-3">Checks</td>
                    <td className="py-3"><span className="bg-green-900/50 text-green-400 px-2 py-1 rounded text-xs">Read</span></td>
                    <td className="py-3">Confirm CI/CD tests passed before merge.</td>
                  </tr>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-3">Administration</td>
                    <td className="py-3"><span className="bg-green-900/50 text-green-400 px-2 py-1 rounded text-xs">Read</span></td>
                    <td className="py-3">Monitor branch protection rules for drift.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-12 text-center">
              <p className="text-slate-400 mb-4">Ready to audit securely?</p>
              <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                Connect Read-Only Integration
              </a>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      <section className={tokens.section}>
        <div className={tokens.container}>
          <p className="text-slate-400 text-center mb-6">
            Need to share evidence externally? Review our{' '}
            <Link to="/secure-evidence-sharing" className="text-primary-400 hover:underline">secure evidence sharing</Link>
            approach.
          </p>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Security Topics</h2>
          <div className="grid md:grid-cols-5 gap-6">
            <Link to="/features/drift-detection" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Drift Detection</h4>
              <p className="text-sm text-slate-400">How we monitor for unauthorized changes to security controls.</p>
            </Link>
            <Link to="/outsourced-software-change-control" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Outsourced Audit</h4>
              <p className="text-sm text-slate-400">Auditing external vendors without granting them extra access.</p>
            </Link>
            <Link to="/tools/branch-protection-snapshot" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Branch Protection Tool</h4>
              <p className="text-sm text-slate-400">Snapshot your current security posture instantly.</p>
            </Link>
            <Link to="/solutions" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Solutions</h4>
              <p className="text-sm text-slate-400">Explore compliance-focused evidence workflows.</p>
            </Link>
            <Link to="/security" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Security</h4>
              <p className="text-sm text-slate-400">Read-only access and data minimization boundaries.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
